import React, { useMemo, useRef } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import type {
  ScheduleTableRow,
  ScheduleActivityRow,
} from "../../../../models";
import {
  formatDate,
  formatDuration,
  statusLabel,
  statusBadgeClass,
  floatClass,
  floatDays,
} from "../../../../utils/format";
import ScrollSentinel from "../../../../components/ScrollSentinel/ScrollSentinel";
import "./VirtualScheduleTable.css";

interface Props {
  rows: ScheduleTableRow[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  search: string;
}

const columnHelper = createColumnHelper<ScheduleActivityRow>();

// First-paint estimates; the virtualizer remeasures each rendered row, so the
// scrollbar ends up exact regardless of these.
const EST_WBS_ROW = 32;
const EST_ACT_ROW = 25;

// Rows kept mounted beyond the viewport, above and below. Large enough that a
// fast scroll/fling stays ahead of rendering (no blank rows), small enough that
// the DOM stays light. Bump toward 100 if a flash still shows on hard flings.
const OVERSCAN_ROWS = 50;

const VirtualScheduleTable: React.FC<Props> = ({
  rows,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  sorting,
  onSortingChange,
  search,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Rows arrive already searched + paginated from the server; the table just
  // needs the activity rows for column sizing / sort state.
  const activityRows = useMemo(
    () =>
      rows.filter(
        (r): r is ScheduleActivityRow => r.row_type === "activity"
      ),
    [rows]
  );

  // Shallowest WBS depth in view, so the visible hierarchy always starts at
  // level 0 — whether at the full tree (top = depth 1) or scoped into a subtree
  // (scope root = depth 0). Header color/font/indent key off this relative level.
  const minWbsDepth = useMemo(() => {
    let min = Infinity;
    for (const r of rows) {
      if (r.row_type === "wbs" && r.depth < min) min = r.depth;
    }
    return min === Infinity ? 0 : min;
  }, [rows]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("task_code", {
        header: "Activity ID",
        size: 100,
      }),
      columnHelper.accessor("task_name", {
        header: "Activity Name",
        size: 350,
        enableSorting: false,
        cell: (info) => {
          const tf = info.row.original.total_float;
          const isCritical = tf !== null && tf <= 0;
          return (
            <span className="activity-name-cell">
              {isCritical && (
                <span className="cp-pill" title="On the critical path">
                  CP
                </span>
              )}
              <span className="activity-name-text">{info.getValue()}</span>
            </span>
          );
        },
      }),
      columnHelper.accessor("status", {
        header: "Status",
        size: 90,
        cell: (info) => (
          <span className={statusBadgeClass(info.getValue())}>
            {statusLabel(info.getValue())}
          </span>
        ),
      }),
      columnHelper.accessor("remaining_duration", {
        header: "Rem Dur",
        size: 70,
        cell: (info) => formatDuration(info.getValue()),
      }),
      columnHelper.accessor("original_duration", {
        header: "Orig Dur",
        size: 70,
        cell: (info) => formatDuration(info.getValue()),
      }),
      columnHelper.accessor("display_start", {
        header: "Start",
        size: 95,
        enableSorting: false,
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor("display_finish", {
        header: "Finish",
        size: 95,
        enableSorting: false,
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor("task_type", {
        header: "Type",
        size: 80,
        enableSorting: false,
      }),
      columnHelper.accessor("total_float", {
        header: "TFloat",
        size: 55,
        cell: (info) => {
          const tf = info.getValue();
          if (tf === null) return "";
          return <span className={floatClass(tf)}>{floatDays(tf)}</span>;
        },
      }),
      columnHelper.accessor("free_float", {
        header: "FFloat",
        size: 55,
        cell: (info) => {
          const ff = info.getValue();
          if (ff === null) return "";
          return <span>{floatDays(ff)}</span>;
        },
      }),
      columnHelper.accessor("actual_start", {
        header: "Act Start",
        size: 95,
        enableSorting: false,
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor("actual_finish", {
        header: "Act Finish",
        size: 95,
        enableSorting: false,
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor("early_start", {
        header: "Early Start",
        size: 95,
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor("early_finish", {
        header: "Early Finish",
        size: 95,
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor("target_start", {
        header: "Target Start",
        size: 95,
        enableSorting: false,
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor("target_finish", {
        header: "Target Finish",
        size: 95,
        enableSorting: false,
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor("late_start", {
        header: "Late Start",
        size: 95,
        enableSorting: false,
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor("late_finish", {
        header: "Late Finish",
        size: 95,
        enableSorting: false,
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor("expect_end_date", {
        header: "Expected End",
        size: 105,
        enableSorting: false,
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.display({
        id: "constraint",
        header: "Constraint",
        size: 130,
        cell: ({ row }) => {
          const { cstr_type, cstr_date } = row.original;
          if (!cstr_type) return "";
          const abbr = cstr_type.replace(/^CS_/, "");
          const date = formatDate(cstr_date);
          return date ? `${abbr}: ${date}` : abbr;
        },
      }),
      columnHelper.accessor("section_name", {
        header: "Takt Section",
        size: 160,
        enableSorting: false,
        cell: (info) => info.getValue() ?? "",
      }),
      columnHelper.accessor("takt_a", {
        header: "Takt A",
        size: 70,
        enableSorting: false,
        cell: (info) => info.getValue() ?? "",
      }),
      columnHelper.accessor("takt_b", {
        header: "Takt B",
        size: 80,
        enableSorting: false,
        cell: (info) => info.getValue() ?? "",
      }),
      columnHelper.accessor("takt_c", {
        header: "Takt C",
        size: 70,
        enableSorting: false,
        cell: (info) => info.getValue() ?? "",
      }),
      columnHelper.accessor("takt_work", {
        header: "Takt Work",
        size: 80,
        enableSorting: false,
        cell: (info) => info.getValue() ?? "",
      }),
    ],
    []
  );

  const table = useReactTable({
    data: activityRows,
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      const newSorting =
        typeof updater === "function" ? updater(sorting) : updater;
      onSortingChange(newSorting);
    },
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
  });

  // Map activity id -> TanStack row, so each virtualized activity row can pull
  // its rendered cells.
  const tanstackRows = table.getRowModel().rows;
  const activityRowMap = useMemo(() => {
    const map = new Map<number, (typeof tanstackRows)[0]>();
    for (const tr of tanstackRows) map.set(tr.original.id, tr);
    return map;
  }, [tanstackRows]);

  // Window the interleaved row list so only the visible slice is in the DOM.
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) =>
      rows[index]?.row_type === "wbs" ? EST_WBS_ROW : EST_ACT_ROW,
    overscan: OVERSCAN_ROWS,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom =
    virtualItems.length > 0
      ? totalSize - virtualItems[virtualItems.length - 1].end
      : 0;

  const colCount = columns.length;

  const renderRow = (row: ScheduleTableRow, index: number) => {
    if (row.row_type === "wbs") {
      const level = Math.max(0, row.depth - minWbsDepth);
      return (
        <tr
          key={`wbs-${index}`}
          data-index={index}
          ref={rowVirtualizer.measureElement}
          className={`wbs-group-row wbs-level-${level % 3}`}
        >
          <td
            colSpan={colCount}
            style={{
              paddingLeft: `${8 + level * 14}px`,
              fontSize: `${Math.max(10, 13 - level)}px`,
            }}
          >
            {level > 0 && (
              <span className="wbs-guide" aria-hidden="true">
                {"│ ".repeat(level)}
              </span>
            )}
            <strong>{row.name}</strong>
            <span className="wbs-count">{row.activity_count}</span>
          </td>
        </tr>
      );
    }
    return (
      <tr
        key={`act-${row.id}`}
        data-index={index}
        ref={rowVirtualizer.measureElement}
        className={
          row.total_float !== null && row.total_float <= 0 ? "critical-row" : ""
        }
      >
        {activityRowMap.get(row.id)?.getVisibleCells().map((cell) => (
          <td key={cell.id} style={{ width: cell.column.getSize() }}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        ))}
      </tr>
    );
  };

  return (
    <div className="table-scroll" ref={scrollRef}>
      <table className="activity-table">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((header) => (
                <th
                  key={header.id}
                  style={{ width: header.getSize() }}
                  onClick={header.column.getToggleSortingHandler()}
                  className={header.column.getCanSort() ? "sortable" : ""}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                  {header.column.getIsSorted() === "asc" && " ▲"}
                  {header.column.getIsSorted() === "desc" && " ▼"}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {paddingTop > 0 && (
            <tr aria-hidden="true" className="schedule-spacer-row">
              <td
                className="schedule-spacer-cell"
                colSpan={colCount}
                style={{ height: paddingTop }}
              />
            </tr>
          )}
          {virtualItems.map((vi) => renderRow(rows[vi.index], vi.index))}
          {paddingBottom > 0 && (
            <tr aria-hidden="true" className="schedule-spacer-row">
              <td
                className="schedule-spacer-cell"
                colSpan={colCount}
                style={{ height: paddingBottom }}
              />
            </tr>
          )}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={colCount} className="schedule-empty">
                {search.trim()
                  ? "No activities match your search."
                  : "No activities to display."}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {hasMore && (
        <ScrollSentinel
          onVisible={onLoadMore}
          loading={loading || loadingMore}
          scrollSelector=".table-scroll"
          prefetchPx={1000}
        />
      )}
    </div>
  );
};

export default VirtualScheduleTable;
